import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardBody, CardFooter, Dialog, Input, Typography } from "@material-tailwind/react";
import { motion, AnimatePresence } from "framer-motion";
import Label from "components/Label/Label";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  adminGetUnapprovedLocations,
  resetValues,
  updateLocationCommission,
} from "redux/features/location/locationSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { z } from "zod";
import { EASING } from "../../../utils/easingUtils";

// Animation variants
const dialogVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: EASING.easeOut } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: EASING.easeIn } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASING.easeOut } },
};

const formSchema = z.object({
  commission: z.string(),
});

interface UpdateModalProps {
  onClose: (value: boolean) => void;
  id?: any;
  title?: any;
  locationData?: any;
}

export const UpdateLocationsCommissionModal: React.FC<UpdateModalProps> = React.memo(
  ({ onClose, id, locationData }) => {
    const {
      register,
      handleSubmit,
      formState: { errors },
      reset,
    } = useForm<z.infer<typeof formSchema>>({
      resolver: zodResolver(formSchema),
      defaultValues: { commission: "" },
      mode: "onSubmit",
    });

    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(true);

    const locationState = useAppSelector((state) => state.location);

    useEffect(() => {
      switch (locationState.loading) {
        case "pending":
          setLoading(true);
          break;
        case "succeeded":
        case "failed":
          setLoading(false);
          break;
      }
    }, [locationState.loading]);

    useEffect(() => {
      if (open) {
        reset({ commission: "" });
      }
    }, [open, reset]);

    const handleClose = () => {
      setOpen(false);
      onClose(false);
    };

    const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
      if (id) {
        setLoading(true);
        await dispatch(
          updateLocationCommission({
            data,
            id: id,
          })
        ).then(() => {
          setLoading(false);
          setOpen(false);
          onClose(false);
          dispatch(adminGetUnapprovedLocations({ page: 1, title: "" }));
          dispatch(resetValues());
        });
      }
    };

    return (
      <AnimatePresence>
        {open && (
          <Dialog
            size="sm"
            open={open}
            handler={handleClose}
            className="w-full max-w-md mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
            aria-labelledby="dialog-title"
          >
            <motion.div variants={dialogVariants} initial="hidden" animate="visible" exit="exit">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Card className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
                  <CardBody className="flex flex-col gap-6 p-6 max-h-[50vh]  bg-white dark:bg-gray-900 rounded-t-2xl">
                    <Typography
                      id="dialog-title"
                      variant="h5"
                      className="p-4 font-bold tracking-tight text-center text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl animate-fade-in"
                    >
                      Espace : {locationData.title}
                    </Typography>
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Commission :</Label>
                      <div className="relative mt-2">
                        <Input
                          size="lg"
                          type="text"
                          value={locationData?.commission}
                          disabled
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                    </motion.div>
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">
                        Nouvelle Commission :
                      </Label>
                      <div className="relative mt-2">
                        <Input
                          size="lg"
                          type="text"
                          placeholder="10%"
                          {...register("commission")}
                          aria-invalid={!!errors.commission}
                          aria-describedby="commission-error"
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.commission && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              id="commission-error"
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.commission.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </CardBody>
                  <CardFooter className="flex justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
                    <ButtonSecondary
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
                      disabled={loading}
                    >
                      Annuler
                    </ButtonSecondary>
                    <ButtonSecondary
                      type="submit"
                      className="px-6 py-2 text-white transition-all duration-300 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800 hover:shadow-lg"
                      disabled={loading}
                      loading={loading}
                    >
                      Mettre à jour
                    </ButtonSecondary>
                  </CardFooter>
                </Card>
              </form>
            </motion.div>
          </Dialog>
        )}
      </AnimatePresence>
    );
  }
);

export default UpdateLocationsCommissionModal;
